'use client'
import {
  OobDisplayTokenFragment,
  ProjectCmsDetailFragment,
  ProjectDetailFragment,
  Render_Types_Enum,
  SoloViewerTokenNoProjectFragment
} from '@/gql/generated/graphql'
import { formatDate, getInvocationFromTokenId, getMinterName, getNonLiveDisplayType } from '@utils/common'
import {
  BodyText,
  DetailText,
  HeadlineText,
  ImageWithCustomLoader,
  LeftArrowIcon
} from 'design-configuration/components'
import React from 'react'
import { AllPurposeLink } from '@components/all-purpose-link'
import { Button, buttonVariants } from '@components/button'

import { motion } from 'motion/react'
import { MarkdownDisplay } from '@components/markdown-text'
import { artistRoute, collectionsRoute, exhibitionShowRoute, getEtherscanAddressURL } from '@utils/routes'
import { isAb500, isMarfaResidency, ValidStatusType } from '@utils/project'
import { STATUS_REFRESH_INTERVAL_SECONDS, useProjectStatus } from '@/hooks/use-project-status'
import { TokenDisplay } from '@components/token-display'
import SwitchToggle from '@components/switch-toggle'
import { useSearchParams } from 'next/navigation'
import { cn } from '@/lib/utils'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { TextButton } from '@components/text-button'
import { FALLBACK_PROFILE_PIC } from '@consts/common'

export type ProjectDetailsProps = {
  project: ProjectDetailFragment
  projectCms?: ProjectCmsDetailFragment
}

const minterDetailsText = (invocations: string, minterName: string, date?: Date) => {
  const now = new Date()
  const releaseCopy = date ? (date > now ? 'releasing' : 'released') : ''
  const dateFormatted = date
    ? formatDate(
        date,
        {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        },
        'en-UK'
      )
    : null
  const dateCopy = dateFormatted ? ` on ${dateFormatted}` : ''

  // Build the string parts and filter out empty ones
  const parts = [`${invocations} works`, releaseCopy, minterName ? `with ${minterName}` : '', dateCopy].filter(Boolean)
  return parts.join(' ')
}

export const ProjectAbout = ({ project, projectCms }: ProjectDetailsProps): React.ReactElement => {
  const { status, refetchStatus } = useProjectStatus(project)

  React.useEffect(() => {
    const interval = setInterval(() => {
      refetchStatus()
    }, STATUS_REFRESH_INTERVAL_SECONDS * 1000)

    if (status === ValidStatusType.complete) {
      clearInterval(interval)
      return
    }

    return () => clearInterval(interval)
  }, [refetchStatus, status])

  const tokens = status === ValidStatusType.upcoming ? project.oob_tokens : project.tokens
  const stickyToken = tokens && tokens.length > 0 ? tokens[1 % tokens.length] : undefined
  const bgToken = project.tokens?.[project.tokens.length - 1]
  const tags = project.tags.filter((tag) => !!tag.tag && tag?.tag?.name !== 'ab500')
  const startDate = project.start_datetime ?? project.auction_start_time
  const isPostParamProject = project.pmp_project_configs?.length

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
      className="gap-x-xl flex w-full items-start justify-center"
    >
      <div className="gap-y-xl p-md lg:p-2xl lg:gap-y-2xl flex w-full flex-col items-start justify-center lg:w-1/2">
        <MarkdownDisplay
          componentOverrides={{
            h1: ({ children }) => <HeadlineText>{children}</HeadlineText>,
            h2: ({ children }) => <HeadlineText>{children}</HeadlineText>,
            h3: ({ children }) => <HeadlineText>{children}</HeadlineText>
          }}
        >
          {project.description}
        </MarkdownDisplay>
        {isMarfaResidency(project.id) ? <MarfaResidencyCallout /> : null}
        {isAb500(project) ? <AB500Callout /> : null}
        <div className="w-full [&>*:first-child]:pt-0">
          {project.exhibition?.data?.length ? (
            <AboutGridItem
              title="FROM THE EXHIBITION"
              content={
                <AllPurposeLink
                  href={exhibitionShowRoute(project.exhibition?.data[0]?.attributes?.slug ?? '')}
                  className="gap-x-2xs flex"
                >
                  <BodyText>{project.exhibition?.data[0]?.attributes?.Title}</BodyText>
                  <LeftArrowIcon className="rotate-[135deg]" />
                </AllPurposeLink>
              }
            />
          ) : null}
          {isPostParamProject ? (
            <AboutGridItem
              title="ABOUT LIVING ART WITH POSTPARAMS"
              description={
                'Artworks in this collection can grow and change over time through artist-defined interactions. This might include allowing the artist or collectors to customize certain elements, responding to blockchain activity, or both—creating unique relationships that evolve with each piece.'
              }
              content={
                <BodyText className="text-on-surface-variant">
                  Learn more about living art and PostParams{' '}
                  <AllPurposeLink
                    className="underline"
                    href={'https://www.artblocks.io/articles/postparams-debuts-jiwa-opensea-living-art'}
                  >
                    here
                  </AllPurposeLink>
                  .
                </BodyText>
              }
            />
          ) : null}
          <AboutGridItem title="LIBRARY" description={project.script_type_and_version} />
          <AboutGridItem title="DISPLAY NOTES" description={project.artist_display_notes} />
          <AboutGridItem title="CREATIVE CREDITS" description={project.creative_credit} />
          <AboutGridItem title="SALES NOTES" description={project.sales_notes} />
          <AboutGridItem
            title="RELEASE DETAILS"
            content={
              projectCms?.AuctionInfoRow?.length ? (
                <div className="gap-y-xs flex flex-col">
                  {projectCms.AuctionInfoRow.map((row, index) => {
                    return (
                      <BodyText key={index}>
                        {minterDetailsText(
                          (
                            row?.MinterMaxInvocations ??
                            project.minter_configuration?.max_invocations ??
                            project.max_invocations
                          ).toString(),
                          row?.CustomAuctionName ?? row?.MinterType?.data?.attributes?.Name ?? '',
                          new Date(row?.AuctionStartTime)
                        )}
                      </BodyText>
                    )
                  })}
                </div>
              ) : (
                <BodyText>
                  {minterDetailsText(
                    project.max_invocations.toString(),
                    project.minter_configuration?.minter?.minter_type
                      ? getMinterName(project.minter_configuration?.minter?.minter_type)
                      : '',
                    startDate ? new Date(startDate) : undefined
                  )}
                </BodyText>
              )
            }
          />
          <AboutGridItem title="CHARITABLE GIVING" description={project.charitable_giving_details} />
          <AboutGridItem
            title="TAGS"
            content={
              tags.length ? (
                <div className="gap-xs flex flex-wrap">
                  {tags.map((tag, index) =>
                    tag.tag ? (
                      <AllPurposeLink
                        href={collectionsRoute(
                          '?' +
                            new URLSearchParams({
                              [tag.tag?.grouping_name === 'heritage' && tag.tag?.name?.includes('series')
                                ? 'series'
                                : tag.tag?.grouping_name === 'heritage'
                                  ? 'category'
                                  : 'tags']: tag.tag?.name ?? ''
                            }).toString()
                        )}
                        key={index}
                        className="border-outline-variant px-xs relative rounded-full border text-center"
                      >
                        <BodyText key={index}>{tag?.tag?.display_name}</BodyText>
                      </AllPurposeLink>
                    ) : null
                  )}
                </div>
              ) : undefined
            }
          />
          <AboutGridItem
            title="ARTIST SITE"
            content={
              project.website ? (
                <AllPurposeLink href={project.website ?? ''} className="gap-x-2xs flex flex-wrap">
                  <BodyText>{project.website.slice(0, 37) + '...'}</BodyText>
                  <LeftArrowIcon className="rotate-[135deg]" />
                </AllPurposeLink>
              ) : undefined
            }
          />
          <AboutGridItem
            title="CONTRACT"
            content={
              <AllPurposeLink href={getEtherscanAddressURL(project.contract_address)} className="gap-x-2xs flex">
                <BodyText>Etherscan</BodyText>
                <LeftArrowIcon className="rotate-[135deg]" />
              </AllPurposeLink>
            }
          />
          <AboutGridItem
            title="LICENSE"
            content={
              project.license ? (
                project.link_to_license ? (
                  <AllPurposeLink href={project.link_to_license} className="gap-x-2xs flex">
                    <BodyText>{project.license}</BodyText>
                    <LeftArrowIcon className="rotate-[135deg]" />
                  </AllPurposeLink>
                ) : (
                  <BodyText>{project.license}</BodyText>
                )
              ) : undefined
            }
          />
        </div>
        {project.artistPage?.data.length ? null : (
          <div className="relative flex min-h-[500px] w-full items-center justify-center">
            <div className="absolute inset-0 h-full w-full">
              <ImageWithCustomLoader
                src={bgToken?.media_url ?? ''}
                style={{
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                priority
                fill
                alt={`${bgToken?.token_id}`}
                sizes="50vw"
                blurHash={bgToken?.image?.metadata?.blurhash}
              />
            </div>
            <div className="px-md z-10 w-full py-[80px]">
              <div className="bg-surface-inverted gap-y-md rounded-xs py-2xl px-md flex w-full flex-col items-center justify-center shadow-[3px_3px_6px_0px_rgba(0,0,0,0.2)]">
                <div className="relative h-[160px] w-[160px] flex-shrink-0">
                  <ImageWithCustomLoader
                    src={project.artist?.profile_by_id?.profile_picture?.url ?? FALLBACK_PROFILE_PIC}
                    alt={`${project.artist_name}'s profile picture`}
                    className="rounded-full"
                    fill
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <div className="text-on-surface-inverted flex h-full w-full flex-col items-center justify-center">
                  <DetailText className="text-on-surface-variant font-medium"> THE ARTIST</DetailText>
                  <HeadlineText className="py-xs">{project.artist_name ?? ''}</HeadlineText>
                  {project.artist?.profile_by_id?.bio ? (
                    <BodyText>
                      {project.artist?.profile_by_id?.bio?.substring(0, 400) +
                        (project.artist?.profile_by_id?.bio?.length > 400 ? '...' : '')}
                    </BodyText>
                  ) : null}
                </div>
                <AllPurposeLink
                  href={artistRoute(project.artist_address ?? '')}
                  className={cn(
                    buttonVariants({ colorTheme: 'outline' }),
                    'outline-primary-inverted gap-x-2xs bg-transparent" flex items-center'
                  )}
                  onClick={() => {
                    trackButtonClicked(TrackedButtonNames.artistLearnMore, {
                      project,
                      customProperties: {
                        'Artist Address': project.artist_address ?? '',
                        'Artist Name': project.artist_name ?? ''
                      }
                    })
                  }}
                >
                  <BodyText className="text-on-surface-inverted" asChild>
                    <span>Learn more</span>
                  </BodyText>
                </AllPurposeLink>
              </div>
            </div>
          </div>
        )}
      </div>
      <StickyTokenView token={stickyToken} project={project} projectStatus={status} />
    </motion.div>
  )
}

const StickyTokenView = ({
  token,
  project,
  projectStatus
}: {
  token?: SoloViewerTokenNoProjectFragment | OobDisplayTokenFragment
  project: ProjectDetailFragment
  projectStatus: ValidStatusType
}) => {
  const [showLiveView, setShowLiveView] = React.useState(project.primary_render_type === Render_Types_Enum.Live)
  const searchParams = useSearchParams()

  const handleViewAll = () => {
    // Create a new URLSearchParams object with the current query parameters
    const params = new URLSearchParams(searchParams.toString())
    params.set('tab', 'Artworks')
    window.history.pushState(null, '', `?${params.toString()}`)
  }

  if (!token) {
    return null
  }

  return (
    <div className={`bg-surface-low sticky top-0 hidden h-screen w-1/2 flex-col items-center lg:flex`}>
      <div className={`relative w-full flex-1`}>
        <div className="pt-md pb-md pl-md pr-md lg:pl-2xl lg:pr-2xl lg:pt-2xl absolute inset-0">
          <TokenDisplay
            token={token}
            project={project}
            preferredDisplayType={showLiveView ? Render_Types_Enum.Live : getNonLiveDisplayType(project, token)}
            loadingScreen={false}
            autoPlay={true}
          />
        </div>
      </div>
      <div className="p-md gap-x-md flex w-full items-center justify-between">
        <BodyText className="text-on-surface-variant">
          {`${project.name} ${projectStatus === ValidStatusType.upcoming ? 'sample' : `#${getInvocationFromTokenId(token?.token_id ?? '0')}`}`}
        </BodyText>
        <div className="gap-x-sm flex shrink-0 items-center">
          <SwitchToggle
            leftLabel="Live view"
            switchedRight={showLiveView}
            onSwitchChange={() => {
              setShowLiveView(!showLiveView)
            }}
            className=""
          />
          <Button colorTheme={'outline'} className="bg-transparent" onClick={handleViewAll}>
            <BodyText className="text-primary-variant" asChild>
              <span>View all</span>
            </BodyText>
          </Button>
        </div>
      </div>
    </div>
  )
}

export const AboutGridItem = ({
  title,
  description,
  content
}: {
  title: string
  description?: React.ReactElement | string | null
  content?: React.ReactElement
}): React.ReactElement | null => {
  if (!description && !content) {
    return null
  }
  return (
    <div className="py-md border-outline-variant gap-y-md gap-x-md flex w-full flex-col items-start border-b">
      <DetailText className="text-on-surface-variant shrink-0 font-medium leading-[1.3rem]">{title}</DetailText>
      {description ? <BodyText>{description}</BodyText> : null}
      {content ? content : null}
    </div>
  )
}

const MarfaResidencyCallout = (): React.ReactElement => (
  <AboutGridItemCallout
    title="Art Blocks × OpenSea Artist Residency Program"
    description="This release was supported by the Art Blocks × OpenSea Artist Residency program, which provides generative artists with individual residencies in Marfa, Texas to develop new work while surrounded by the inspiring Marfa environment and community."
  />
)

const AB500Callout = (): React.ReactElement => (
  <AboutGridItemCallout
    title="The Art Blocks 500"
    description="This release is part of the Art Blocks 500. The Art Blocks 500 represents five years of groundbreaking generative art on the blockchain. From 2020 through 2025, these 500 collections capture the complete evolution of on-chain generative art, showcasing pioneering artists, innovative algorithms, and the creative community that defined a movement."
    content={
      // TODO: Add link to the correct article
      <AllPurposeLink href="https://www.artblocks.io">
        <TextButton className="text-on-surface-variant flex-shrink-0">
          <span>Learn more about the Art Blocks 500</span>
        </TextButton>
      </AllPurposeLink>
    }
  />
)

const AboutGridItemCallout = ({
  title,
  description,
  content
}: {
  title: string
  description?: React.ReactElement | string | null
  content?: React.ReactElement
}): React.ReactElement | null => {
  if (!description && !content) {
    return null
  }
  return (
    <div className="p-sm border-outline-variant gap-y-xs bg-surface-container flex w-full flex-col items-start">
      <DetailText className="text-on-surface-variant shrink-0 font-medium uppercase leading-[1.3rem]">
        {title}
      </DetailText>
      {description ? <BodyText>{description}</BodyText> : null}
      {content ? content : null}
    </div>
  )
}
